import { isPRODDeploy } from '@/lib/constants'

import { getCurrentTenant, useMaybeCurrentTenant } from '@/shared/auth/store'

/**
 * Temporary per-tenant gate for the network logging screens (Network
 * Collectors, Log Sources, the Network Logs tab and the Integrations
 * Overview, which leads with collector health).
 *
 * Prod only: on prod, a tenant sees the screens only if its ID (the `?t=`
 * slug) is in this list. A tenant created after the list was captured is
 * excluded until someone adds it here. Every other stage (local, dev,
 * staging, demo) lets every tenant through, so QA and the demo keep the
 * feature.
 *
 * Gated tenants get the pre-1.32 Integrations surface: a single "Management"
 * sidebar entry pointing at `/integrations/cloud`, with the Overview and
 * collector routes redirecting there.
 *
 * A deliberate exception to ADR 0030, which lists neither a build flag nor a
 * hardcoded tenant list as a gate source. The backend `tenant.features` flag
 * would be the canonical home; this list is the UI-only stopgap.
 *
 * NOT a security boundary — the collector endpoints still answer for any
 * tenant. The plan gate (`collectors/plan-upsell.ts`) still applies on top: an
 * allowlisted tenant whose plan excludes syslog sees no Network Logs tab.
 *
 * Captured from the prod tenant switcher on 2026-10-07.
 *
 * ENDS WHEN: network logging is released to every tenant — delete this file
 * and its call sites (`git grep network-logs-allowlist`).
 */
export const NETWORK_LOGS_TENANT_IDS: ReadonlySet<string> = new Set([
	'acu1', // Abilene Christian University
	'ehwmre1sft', // Ameritruck
	'asba', // Aspire Bakeries
	'rgekr4mo', // Bayside
	'bylt', // Bitlyft Security
	'blck', // Blackstone Technology Group
	'x6x76i43', // BrownStone Engineering
	'gbcp', // Calidris Investment Partners
	'gbci', // Chard RE
	'clpw', // City Light & Power
	'cbpu', // Coldwater Board of Public Utilities
	'comp', // Compliancy IT
	'cmrd', // Comrod
	'copm', // Copen Machine Inc
	'gcls', // Current Lighting
	'ebtc', // EBTECH
	'elmt', // Elmet
	'elzy', // ELZLY
	'gbfr', // Fergus RE
	'gbge', // Geneva
	'gnbe', // Gnosis
	'heki', // Hek INC
	'hhau', // Houston Housing Authority
	'inwu', // Indiana Wesleyan University
	'jag1', // JAG
	'bv1tjl62', // LIGD - AP-Southeast-2
	'jk526vo6', // LIGD - CA-Central-1
	'srh9turr', // LIGD - EU-Central-1
	'xniosdsb', // LIGD - EU-West-2
	'x9yi3bdc', // LIGD - US-West-2
	'ltsp', // Lightship
	'linm', // Linear Motion
	'ligd', // Liongard
	'mcgo', // MCGO
	'zlhx326sdh', // Mike Noel
	'msda', // Mississippi Development Authority
	'gbmp', // Monarch Point
	'mtow', // Monterey One Water
	'nhch', // New Hope Church
	'fx8n2vjc', // Ohio Valley Land Services
	'omni', // OMNI Pain and Precision
	'petr', // PETRAP
	'prct', // PRECT
	'gbpc', // Primary Commodity Fund
	'rave', // RAVE
	'gbrh', // Rawlinson-Hunter
	'rmsl', // RMSL
	'gbsi', // Saltzman Insights
	'scfh', // Santa Clara Family Health
	'scri', // Scripps Research
	'scoa', // Shiawassee Council on Aging
	'sowu', // Southern Wesleyan University (Staff)
	'sowus', // Southern Wesleyan University (Students)
	'step', // STEP
	'smg1', // Success Group Mortgage
	'tcxp', // TechnologyXperts
	'tsbk', // The State Bank
	'uomu', // University of Mount Union
	'wlns', // Wellness Center
])

/**
 * Whether `tenant` may see the network logging screens. Fails closed on prod
 * while the tenant is unresolved. Reads `isPRODDeploy` per call (not at module
 * load) so tests can flip the stage.
 */
export function isNetworkLogsEnabled(tenant: string | undefined): boolean {
	if (!isPRODDeploy) return true
	return tenant !== undefined && NETWORK_LOGS_TENANT_IDS.has(tenant)
}

/**
 * Whether `pathname` is a network logging screen the Integrations layout
 * redirects a gated tenant away from: the Overview (it leads with collector
 * health) and every collector route. Collectors match on the `/` boundary (the
 * same idea as `DYNAMIC_ROUTE_PARENTS` in `post-switch-path.ts`), so a sibling
 * that only shares the prefix (`/integrations/collectorsX`) is not swept up.
 */
export const isNetworkLogsPath = (pathname: string): boolean =>
	pathname === '/integrations' ||
	pathname === '/integrations/' ||
	pathname === '/integrations/collectors' ||
	pathname.startsWith('/integrations/collectors/')

/** The gate for the active tenant, in React. Safe in any render context. */
export const useNetworkLogsEnabled = (): boolean =>
	isNetworkLogsEnabled(useMaybeCurrentTenant())

/**
 * The gate for the active tenant, outside React — for `beforeLoad` and
 * loaders. Only meaningful after `_dashboard` `beforeLoad` has seeded the
 * tenant; before that it fails closed on prod.
 */
export const getNetworkLogsEnabled = (): boolean =>
	isNetworkLogsEnabled(getCurrentTenant())
